import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPinIcon, ClockIcon, CalendarIcon } from "@/components/Icons";
import PhotoCarousel from "@/components/PhotoCarousel";

// Loads Razorpay Checkout script exactly once
let _rzpLoader = null;
function loadRazorpay() {
    if (_rzpLoader) return _rzpLoader;
    _rzpLoader = new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error("Razorpay SDK failed to load"));
        document.body.appendChild(s);
    });
    return _rzpLoader;
}

export default function CenterDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [center, setCenter] = useState(null);
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedSeat, setSelectedSeat] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/centers/${id}`).then((r) => setCenter(r.data));
        api.get("/slots", { params: { center_id: id } }).then((r) => setSlots(r.data));
    }, [id]);

    const grouped = useMemo(() => {
        const g = {};
        for (const s of slots) {
            g[s.date] = g[s.date] || [];
            g[s.date].push(s);
        }
        return g;
    }, [slots]);

    const openSeatMap = (slot) => {
        setSelectedSlot(slot);
        setSelectedSeat(null);
        setConfirmOpen(true);
    };

    const payWithRazorpay = (booking) => {
        return new Promise(async (resolve, reject) => {
            try {
                await loadRazorpay();
                const { data: order } = await api.post("/payments/razorpay/order", { booking_id: booking.id });
                const options = {
                    key: order.key_id,
                    amount: order.amount,
                    currency: order.currency,
                    order_id: order.order_id,
                    name: order.name,
                    description: order.description,
                    prefill: order.prefill,
                    notes: order.notes,
                    theme: { color: "#002FA7" },
                    handler: async (response) => {
                        try {
                            await api.post("/payments/razorpay/verify", {
                                booking_id: booking.id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            });
                            resolve(true);
                        } catch (e) {
                            reject(new Error("Verification failed"));
                        }
                    },
                    modal: {
                        ondismiss: () => reject(new Error("dismissed")),
                    },
                };
                const rzp = new window.Razorpay(options);
                rzp.on("payment.failed", () => reject(new Error("Payment failed")));
                rzp.open();
            } catch (e) {
                reject(e);
            }
        });
    };

    const confirmBooking = async () => {
        if (!user) {
            toast.error("Please sign in to book");
            nav("/login");
            return;
        }
        if (!selectedSeat) { toast.error("Pick a seat first"); return; }
        setBusy(true);
        try {
            const { data: booking } = await api.post("/bookings", {
                slot_id: selectedSlot.id,
                seat_number: selectedSeat,
            });
            if (booking.price > 0) {
                try {
                    await payWithRazorpay(booking);
                    toast.success("Payment successful — booking confirmed");
                    nav("/my-bookings");
                } catch (e) {
                    if (e.message === "dismissed") {
                        toast.info("Payment cancelled. Your seat reservation is on hold — complete payment from My Bookings.");
                    } else {
                        toast.error(e.message || "Payment failed");
                    }
                }
            } else {
                toast.success("Booking confirmed!");
                nav("/my-bookings");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Booking failed");
        } finally {
            setBusy(false);
        }
    };

    if (!center) return <div className="mx-auto max-w-7xl px-6 py-16 text-muted-foreground">Loading center…</div>;

    const seatsPerRow = center.seats_per_row || 8;

    return (
        <div className="mx-auto max-w-7xl px-6 py-10">
            {/* Center header */}
            <div className="grid md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-5 border border-border overflow-hidden">
                    <PhotoCarousel photos={center.photo_urls} alt={center.name} />
                </div>
                <div className="md:col-span-7">
                    <div className="overline flex items-center gap-1"><MapPinIcon className="h-3 w-3" /> {center.city}</div>
                    <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight">{center.name}</h1>
                    <p className="mt-3 text-muted-foreground">{center.address}</p>
                    {center.description && <p className="mt-4 text-sm leading-relaxed">{center.description}</p>}
                    <div className="mt-6 flex gap-2 flex-wrap">
                        {center.amenities?.map((a) => (
                            <span key={a} className="text-[10px] uppercase tracking-widest border border-border px-2 py-1">{a}</span>
                        ))}
                        <span className="text-[10px] uppercase tracking-widest bg-secondary px-2 py-1 font-mono">{center.total_seats} SEATS</span>
                    </div>
                </div>
            </div>

            {/* Slot picker */}
            <div className="mt-16">
                <div className="overline mb-3">Choose your slot</div>
                <h2 className="font-heading text-2xl font-bold tracking-tight">Available dates & times</h2>

                {Object.keys(grouped).length === 0 ? (
                    <div className="mt-6 border border-dashed border-border p-10 text-center bg-card text-muted-foreground" data-testid="no-slots">
                        No slots posted yet. Check back soon.
                    </div>
                ) : (
                    <div className="mt-6 space-y-8">
                        {Object.entries(grouped).sort().map(([date, day]) => (
                            <div key={date} className="border border-border bg-card">
                                <div className="border-b border-border px-5 py-3 flex items-center gap-2">
                                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                    <div className="font-heading font-bold">{new Date(date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</div>
                                </div>
                                <div className="p-5 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {day.map((s) => {
                                        const avail = s.total_seats - (s.booked_seats?.length || 0);
                                        return (
                                            <button
                                                key={s.id}
                                                disabled={avail <= 0}
                                                onClick={() => openSeatMap(s)}
                                                className="text-left border border-border p-4 hover:border-foreground hover:-translate-y-0.5 transition-all duration-150 disabled:opacity-40 disabled:hover:translate-y-0"
                                                data-testid={`slot-${s.id}`}
                                            >
                                                <div className="flex items-center gap-2 text-sm"><ClockIcon className="h-4 w-4 text-muted-foreground" /> <span className="font-mono">{s.start_time} — {s.end_time}</span></div>
                                                <div className="mt-2 font-heading text-sm font-bold">{s.exam_category}</div>
                                                <div className="mt-3 flex items-center justify-between text-xs">
                                                    <span className="font-mono text-muted-foreground">{avail}/{s.total_seats} left</span>
                                                    <span className="font-bold">{s.price > 0 ? `₹${s.price.toFixed(2)}` : "Free"}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Seat map dialog */}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent className="max-w-2xl rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {selectedSlot ? `${selectedSlot.exam_category} · ${selectedSlot.date} · ${selectedSlot.start_time}` : ""}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedSlot && (
                        <div>
                            <div className="text-xs text-muted-foreground mb-4 flex items-center gap-4">
                                <span className="flex items-center gap-1"><span className="w-3 h-3 border border-border bg-white inline-block" /> Available</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-primary inline-block" /> Selected</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-muted-foreground/50 inline-block" /> Booked</span>
                            </div>
                            <div className="border border-border p-6 bg-secondary/30">
                                <div className="text-center text-[10px] uppercase tracking-[0.3em] text-muted-foreground border-b border-dashed border-border pb-2 mb-4">Invigilator Desk</div>
                                <div
                                    className="grid gap-2 justify-center"
                                    style={{ gridTemplateColumns: `repeat(${seatsPerRow}, minmax(0, 32px))` }}
                                >
                                    {Array.from({ length: selectedSlot.total_seats }, (_, i) => {
                                        const n = i + 1;
                                        const booked = selectedSlot.booked_seats?.includes(n);
                                        const sel = selectedSeat === n;
                                        return (
                                            <button
                                                key={n}
                                                onClick={() => !booked && setSelectedSeat(n)}
                                                disabled={booked}
                                                className={[
                                                    "h-8 w-8 text-[10px] font-mono border",
                                                    booked ? "bg-muted-foreground/50 border-muted-foreground/50 text-white cursor-not-allowed" :
                                                    sel ? "bg-primary text-primary-foreground border-primary" :
                                                    "bg-white border-border hover:border-foreground",
                                                ].join(" ")}
                                                data-testid={`seat-${n}`}
                                            >
                                                {n}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between text-sm">
                                <div>
                                    <div className="overline">Selected seat</div>
                                    <div className="font-mono font-bold text-lg">{selectedSeat ? `S-${String(selectedSeat).padStart(3, "0")}` : "—"}</div>
                                </div>
                                <div className="text-right">
                                    <div className="overline">Amount</div>
                                    <div className="font-heading font-bold text-lg">{selectedSlot.price > 0 ? `₹${selectedSlot.price.toFixed(2)}` : "Free"}</div>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm" data-testid="cancel-book">Cancel</Button>
                        <Button onClick={confirmBooking} disabled={!selectedSeat || busy} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="confirm-book">
                            {busy ? "Booking…" : selectedSlot?.price > 0 ? "Pay & Book" : "Confirm booking"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
